import { useCallback, useEffect, useReducer } from 'react';
import { useState } from 'react';

import Dialog from 'components/Dialog';
import ToggleSwitch from 'components/ToggleSwitch';
import { Button, PercentInput, TextField } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { DATE_FORMAT } from 'globals/constants';
import {
  deleteAllExportedAttendance,
  exportAllAttendance,
  overwriteAllExportedAttendance,
} from 'services/course-api';

interface Props {
  isOpen: boolean;
  onClose(): void;
}

enum ActionType {
  NUM_SESSION = 'NUM_SESSION',
  POINTS_FOR_EACH_SESSION = 'POINTS_FOR_EACH_SESSION',
  LATE_PENALTY = 'LATE_PENALTY',
  TREAT_EXCUSED_AS_PRESENT = 'TREAT_EXCUSED_AS_PRESENT',
  SYNC_AUTOMATICALLY = 'SYNC_AUTOMATICALLY',
  LAST_EXPORTED = 'LAST_EXPORTED',
}

interface InitialState {
  numSession: number;
  pointsForEachSession: number;
  latePenalty: number;
  treatExcusedAsPresent: boolean;
  syncAutomatically: boolean;
  lastExported: UnixTime;
}

const initialState: InitialState = {
  numSession: 0,
  pointsForEachSession: 1,
  latePenalty: 0,
  treatExcusedAsPresent: true,
  syncAutomatically: true,
  lastExported: 0,
};

interface NumSessionsAction {
  type: ActionType.NUM_SESSION;
  numSession: InitialState['numSession'];
}

interface UpadatePointsAction {
  type: ActionType.POINTS_FOR_EACH_SESSION;
  pointsForEachSession: InitialState['pointsForEachSession'];
}

interface LatePenaltyAction {
  type: ActionType.LATE_PENALTY;
  latePenalty: InitialState['latePenalty'];
}

interface TreatExcusedAsPresentAction {
  type: ActionType.TREAT_EXCUSED_AS_PRESENT;
  treatExcusedAsPresent: InitialState['treatExcusedAsPresent'];
}

interface SyncAutomaticallyAction {
  type: ActionType.SYNC_AUTOMATICALLY;
  syncAutomatically: InitialState['syncAutomatically'];
}

interface LastExportedAction {
  type: ActionType.LAST_EXPORTED;
  lastExported: InitialState['lastExported'];
}

type Action =
  | UpadatePointsAction
  | NumSessionsAction
  | LatePenaltyAction
  | TreatExcusedAsPresentAction
  | SyncAutomaticallyAction
  | LastExportedAction;

const reducer = (state: InitialState, action: Action) => {
  switch (action.type) {
    case ActionType.NUM_SESSION:
      return {
        ...state,
        numSession: action.numSession,
      };
    case ActionType.POINTS_FOR_EACH_SESSION:
      return {
        ...state,
        pointsForEachSession: action.pointsForEachSession,
      };
    case ActionType.LATE_PENALTY:
      return {
        ...state,
        latePenalty: action.latePenalty,
      };
    case ActionType.TREAT_EXCUSED_AS_PRESENT:
      return {
        ...state,
        treatExcusedAsPresent: action.treatExcusedAsPresent,
      };
    case ActionType.SYNC_AUTOMATICALLY:
      return {
        ...state,
        syncAutomatically: action.syncAutomatically,
      };
    case ActionType.LAST_EXPORTED:
      return {
        ...state,
        lastExported: action.lastExported,
      };
    default:
      return state;
  }
};

const ExportAllSessionAttendanceDialog = ({ isOpen, onClose: handleClose }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getAllAttendanceData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await exportAllAttendance();
      dispatch({ type: ActionType.NUM_SESSION, numSession: response.numSessions });
      dispatch({ type: ActionType.POINTS_FOR_EACH_SESSION, pointsForEachSession: response.pointsPerSession });
      dispatch({ type: ActionType.LATE_PENALTY, latePenalty: response.latePenaltyPerc });
      dispatch({
        type: ActionType.TREAT_EXCUSED_AS_PRESENT,
        treatExcusedAsPresent: response.excusedIsPresent === 1,
      });
      dispatch({
        type: ActionType.SYNC_AUTOMATICALLY,
        syncAutomatically: response.automaticSync === 1,
      });
      dispatch({
        type: ActionType.LAST_EXPORTED,
        lastExported: response.lastExported,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleExportAllAttendance = async () => {
    try {
      setIsDisabled(true);
      await overwriteAllExportedAttendance({
        automaticSync: state.syncAutomatically === true ? 1 : 0,
        excusedIsPresent: state.treatExcusedAsPresent === true ? 1 : 0,
        latePenaltyPerc: state.latePenalty,
        pointsPerSession: state.pointsForEachSession,
      });
      handleClose();
    } finally {
      setIsDisabled(false);
    }
  };

  const handleDeleteExportAllAttendance = async () => {
    try {
      setIsDisabled(true);
      await deleteAllExportedAttendance();
      handleClose();
    } finally {
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    if (isOpen) getAllAttendanceData();
  }, [getAllAttendanceData, isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={'Export aggregate attendance'}
      classes={{
        footer: 'export-all-session-body-footer',
      }}
      footer={
        <>
          <p>
            Last exported on :{' '}
            {state.lastExported === -1 ? 'Never' : dt.format(state.lastExported, DATE_FORMAT)}
          </p>
          <div className='footer__buttons'>
            <Button
              type='secondary'
              disabled={state.lastExported === -1 || isDisabled}
              onRipple={handleDeleteExportAllAttendance}
            >
              {state.lastExported === -1 ? 'DELETE' : 'DELETE EXPORTED COLUMN'}
            </Button>
            <Button type='ghost' onRipple={handleExportAllAttendance} disabled={isDisabled}>
              {state.lastExported === -1 ? 'EXPORT SCORES' : 'UPDATE SCORES'}
            </Button>
          </div>
        </>
      }
    >
      <div className='export-all-session-body'>
        {isLoading ? (
          <h3>...Loading attendance</h3>
        ) : (
          <>
            <div className='row'>
              <p>Number of session with attendance</p>
              <p>{state.numSession}</p>
            </div>
            <div className='row'>
              <p>Points for each session</p>
              <TextField
                type='number'
                selector='activity__score-input'
                value={state.pointsForEachSession}
                onchange={(e) => {
                  dispatch({
                    type: ActionType.POINTS_FOR_EACH_SESSION,
                    pointsForEachSession: +e.target.value,
                  });
                }}
                placeholder='Points'
              />
            </div>
            <div className='row'>
              <p>Late penalty percentage</p>
              <PercentInput
                selector='activity__score-input'
                initValue={state.latePenalty}
                onchange={(value) => {
                  dispatch({ type: ActionType.LATE_PENALTY, latePenalty: value });
                }}
                placeholder='Penalty'
              />
            </div>
            <div className='row'>
              <p>Treat "Excused" as "Present"</p>
              <ToggleSwitch
                isOn={state.treatExcusedAsPresent}
                onClick={() => {
                  dispatch({
                    type: ActionType.TREAT_EXCUSED_AS_PRESENT,
                    treatExcusedAsPresent: !state.treatExcusedAsPresent,
                  });
                }}
              />
            </div>
            <div className='row'>
              <p>Sync automatically</p>
              <ToggleSwitch
                isOn={state.syncAutomatically}
                onClick={() => {
                  dispatch({
                    type: ActionType.SYNC_AUTOMATICALLY,
                    syncAutomatically: !state.syncAutomatically,
                  });
                }}
              />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
export default ExportAllSessionAttendanceDialog;
